type GtagFn = (
  command: 'event' | 'config' | 'set',
  targetOrEvent: string,
  params?: Record<string, unknown>,
) => void;

declare global {
  interface Window {
    gtag?: GtagFn;
  }
}

function gtag(...args: Parameters<GtagFn>): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag(...args);
}

/**
 * Set the active language as a GA4 user property. GA4 attaches user_properties
 * to every subsequent event automatically, so this is all that's needed to
 * power user-scoped custom dimensions (e.g. "% of users by language"). Call
 * once at startup and again whenever the user picks a different language from
 * the footer dropdown.
 */
export function setLanguage(language: string): void {
  gtag('set', 'user_properties', { language });
}

/** Send a virtual pageview for the current route + title. */
export function trackPageView(path: string, title: string): void {
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
}

export type TrackEventName =
  | 'form_submitted'
  | 'namecheck_blocked'
  | 'photo_uploaded'
  | 'photo_captured'
  | 'share_downloaded'
  | 'quiz_started'
  | 'quiz_completed'
  | 'quiz_share_downloaded';

/** Track a discrete user action. The active language is already attached via
 * the user_property set in setLanguage(), so callers do not need to pass it. */
export function trackEvent(name: TrackEventName, params: Record<string, unknown> = {}): void {
  gtag('event', name, params);
}
